import React from 'react';
import { DEFAULT_CONTAINER_TYPE_ICON, getImageUrl } from '@/utils/imageUrl';
import { ContainerType } from '../../views/Events/OrderFulfillment/Components/OrderFulfillmentAddContainerTypes';
import i18n from '@/utils/i18n';

export type ContainerTypeReduced = Pick<
  ContainerType,
  'id' | 'name' | 'imageUrl' | 'hexCode'
>;

function getImageSize(size?: 's' | 'm' | 'l' | 'xl' | 'xxl') {
  const sizeConfig = {
    width: 50,
    sizeClasses: 'tw:w-12 tw:h-12',
  };

  switch (size) {
    case 's':
      sizeConfig.width = 40;
      sizeConfig.sizeClasses = 'tw:w-10 tw:h-10';
      break;
    case 'm':
      sizeConfig.width = 50;
      sizeConfig.sizeClasses = 'tw:w-12 tw:h-12';
      break;
    case 'l':
      sizeConfig.width = 80;
      sizeConfig.sizeClasses = 'tw:w-20 tw:h-20';
      break;
    case 'xl':
      sizeConfig.width = 110;
      sizeConfig.sizeClasses = 'tw:w-28 tw:h-28';
      break;
    case 'xxl':
      sizeConfig.width = 256;
      sizeConfig.sizeClasses = 'tw:w-64 tw:h-64';
      break;
    default:
      // Default to medium size
      sizeConfig.width = 50;
      sizeConfig.sizeClasses = 'tw:w-12 tw:h-12';
      break;
  }

  return sizeConfig;
}

type ContainerTypeDisplayProps = {
  containerType: ContainerType | ContainerTypeReduced | undefined;
  showContainerId?: boolean;
  showName?: boolean;
  size?: 's' | 'm' | 'l' | 'xl' | 'xxl';
  showStrikedThrough?: boolean;
  onClick?: () => void;
};

const ContainerTypeDisplay = ({
  containerType,
  showContainerId = true,
  showName = true,
  size = 'm',
  showStrikedThrough = false,
  onClick,
}: ContainerTypeDisplayProps) => {
  const fallbackContainerType = {
    imageUrl: DEFAULT_CONTAINER_TYPE_ICON,
    name: i18n.t('ContainerType:notFoundOrArchived'),
    id: '-',
    hexCode: undefined,
  };

  const { imageUrl, name, id, hexCode } =
    containerType || fallbackContainerType;

  let sizeConfig = getImageSize(size);

  return (
    <div className="tw:flex tw:items-center">
      <div className="tw:relative tw:flex tw:items-center">
        <div
          className={`tw:relative tw:inline-block ${sizeConfig.sizeClasses} ${onClick ? 'tw:cursor-pointer' : ''}`}
          onClick={onClick}
        >
          <img
            className={`tw:w-full tw:h-full tw:object-contain tw:rounded tw:border tw:border-gray-300 ${sizeConfig.sizeClasses}`}
            src={getImageUrl(
              sizeConfig.width,
              imageUrl || DEFAULT_CONTAINER_TYPE_ICON,
            )}
            alt={`Container Type ${name}`}
            title={`Container Type ${name}`}
          />
          {hexCode && (
            <div
              className="tw:absolute tw:inset-0 tw:opacity-30 tw:rounded"
              style={{ backgroundColor: `#${hexCode}` }}
            />
          )}
        </div>
      </div>

      {showName && (
        <div
          className={`tw:flex ${
            !showContainerId ? 'tw:items-center' : 'tw:flex-col tw:items-start'
          } tw:p-2`}
          title={`id: ${id} | name: ${name}`}
        >
          {showStrikedThrough && <p className="tw:line-through">{name}</p>}
          {!showStrikedThrough && (
            <div className="tw:text-sm tw:font-medium">{name}</div>
          )}
          {showContainerId && (
            <div className="tw:text-xs tw:text-gray-500">
              {i18n.t('Common:id')}: {id}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ContainerTypeDisplay;
